import { forwardRef, useState } from 'react';
import { onlyDigits } from '@amai/contracts';
import { dateBRToISO, isoToDateBR, maskDate } from '@/lib/masks';

/**
 * Campo de data em dd/mm/aaaa.
 *
 * O `<input type="date">` do navegador desenha a data na ordem do idioma do
 * SISTEMA, não na do parque. Numa máquina em inglês o operador lia mm/dd/aaaa
 * e lançava 09/12 achando que era 9 de dezembro — e o erro só aparecia no
 * relatório, semanas depois. Aqui a ordem é sempre a nossa.
 *
 * O contrato é o MESMO do campo nativo: entra e sai `aaaa-mm-dd`, que é o que
 * a API recebe. A ordem brasileira é só apresentação, como em toda máscara.
 *
 * Enquanto a data está pela metade o campo vale VAZIO, igual ao nativo: meia
 * data não é data, e a tela não pode achar que tem um valor que não tem.
 */
interface DateInputProps {
  /** Sempre `aaaa-mm-dd`, ou '' quando não há data. */
  value: string | null | undefined;
  /** Recebe `aaaa-mm-dd`, ou '' quando o campo ficou sem data. */
  onChange: (iso: string) => void;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Primeira data aceita, em ISO. Fora do intervalo o campo fica marcado. */
  min?: string;
  /** Última data aceita, em ISO. */
  max?: string;
  disabled?: boolean;
  required?: boolean;
  'aria-label'?: string;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { value, onChange, id, className, style, min, max, disabled, required, ...rest },
  ref,
) {
  // Rascunho: o que está na tela ENQUANTO se digita. Sem ele, o campo voltaria
  // ao valor anterior a cada tecla, porque data pela metade não vira ISO.
  //
  // Ele vive só até o operador sair do campo. Guardar rascunho inválido além
  // disso deixava lixo na tela que nada limpava: no `FilterBar`, digitar uma
  // data pela metade e clicar em "Limpar" não muda o valor (já era ''), então
  // o campo não tinha como saber que precisava se apagar, e continuava
  // vermelho com a data errada.
  const [draft, setDraft] = useState<string | null>(null);
  const iso = value ?? '';

  // O valor mudou por fora — outro registro no drawer, filtro limpo — enquanto
  // o campo estava sendo digitado. Ajuste durante o render, como no FilterBar:
  // sem efeito, sem render extra.
  const [visto, setVisto] = useState(iso);
  if (visto !== iso) {
    setVisto(iso);
    if (draft !== null && dateBRToISO(draft) !== iso) setDraft(null);
  }

  const display = draft ?? isoToDateBR(iso);
  const digits = onlyDigits(display);

  const foraDoIntervalo = (iso: string) => (min && iso < min) || (max && iso > max);

  /** O que dizer ao operador. Só fala quando ele já digitou alguma coisa. */
  const aviso = (() => {
    if (digits.length === 0) return null;
    if (digits.length < 8) return 'Data incompleta: preencha dd/mm/aaaa.';
    const iso = dateBRToISO(digits);
    if (!iso) return 'Essa data não existe. Confira o dia, o mês e o ano.';
    if (min && iso < min) return `A data não pode ser antes de ${isoToDateBR(min)}.`;
    if (max && iso > max) return `A data não pode ser depois de ${isoToDateBR(max)}.`;
    return null;
  })();

  const emitir = (texto: string) => {
    const iso = dateBRToISO(texto);
    onChange(iso && !foraDoIntervalo(iso) ? iso : '');
  };

  return (
    <input
      {...rest}
      ref={ref}
      id={id}
      type="text"
      className={className ?? 'input input--mono'}
      style={style}
      inputMode="numeric"
      autoComplete="off"
      placeholder="dd/mm/aaaa"
      disabled={disabled}
      required={required}
      aria-invalid={aviso ? true : undefined}
      // O aviso vira `title` de propósito: o campo mora em barra de filtro, em
      // grade de formulário e em linha de tabela, e um texto extra embaixo
      // quebraria o alinhamento de um desses três. A borda vermelha avisa que
      // tem coisa errada; o título diz o quê.
      title={aviso ?? undefined}
      value={display}
      onChange={(event) => {
        const texto = maskDate(onlyDigits(event.target.value).slice(0, 8));
        setDraft(texto);
        emitir(texto);
      }}
      // Sair do campo resolve o rascunho, sempre: o que vale é o valor, e o
      // que está na tela passa a ser só a cara dele. O aviso em vermelho e o
      // título aparecem ENQUANTO se digita, que é quando dá para corrigir.
      onBlur={() => setDraft(null)}
    />
  );
});
